/**
 * Provides a selector to add a new tag to the inventory.
 */
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { sortBy } from 'lodash';
import { Select } from 'core/components';
import makeCancelable, { CanceledError } from 'core/util/cancelablePromise';
import { getOption } from 'core/form/components/modalSelect/selectHelpers';
import AssetTagInventoryCollection from 'app/stores/assetTags/AssetTagInventoryCollection';

interface Props {
  collection: AssetTagInventoryCollection;
}

function AddTagSelector({ collection }: Props) {
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);

  /**
   * Loads a complete list of all available tags we can apply as select options.
   * A tag is placed in disabled state if it is already applied to the inventory collection.
   */
  useEffect(() => {
    setLoading(true);

    const fetchRequest = makeCancelable(collection.fetchTagOptions());

    fetchRequest.promise
      .then((tags) => {
        setOptions(tags);
        setLoading(false);
      })
      .catch((err) => {
        if (!(err instanceof CanceledError)) {
          setLoading(false);
          throw err;
        }
      });

    return () => {
      fetchRequest.cancel();
    };
  }, [collection]);

  /**
   * Options are sorted with disabled options pinned to the bottom for easy selection.
   */
  const sortedOptions = sortBy(
    options.map(({ value, label }) => ({ value, label, disabled: !!collection.tags[value] })),
    ['disabled', 'label']
  );

  /**
   * Disable the entire selector if every option has been used.
   */
  const isDisabled = sortedOptions.every((option) => option.disabled);

  /**
   * Adds a new tag to the inventory.
   */
  const onChange = (value: string) => {
    const option = getOption(options, value);

    if (option) {
      const { label, description } = option;
      collection.addTag({ tagId: value, tagName: label, description });
    }
  };

  return (
    <Select
      placeholder="Add Tag"
      options={sortedOptions}
      disabled={isDisabled}
      loading={loading}
      onChange={onChange}
      showFilter
    />
  );
}

export default observer(AddTagSelector);
